import { getAll, getNextId } from "../../localDB";
import appURL from '../../appURL.json';
import { FileStatus, planWorksheetStatusMapping } from "../../visualizations/visualizations";

const customerAccountType = {
    "default": 0,
    "customer": 1,
    "doctor": 2,
    "agent": 3,
    "institution": 4
}

function mapById(array) {
    return (array?.data || array || []).reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
    }, {});
}

export async function transformSyncPresence(rawPresence) {
    if (!rawPresence?.start?.date) {
        return [{}];
    }

    const attendance = await getAll("Attendance");

    const rawDate = new Date(rawPresence?.start?.date);
    rawDate.setHours(0, 0, 0, 0)
    const todayAttendance = attendance.find((a) => {
        const timeInDate = new Date(a?.TimeIn);
        timeInDate.setHours(0, 0, 0, 0); // normalize

        return timeInDate.getTime() === rawDate.getTime();
    });

    const nextID = await getNextId("Attendance");

    return [{
        ID: todayAttendance ? todayAttendance.ID : nextID,
        Date: rawPresence?.start?.date ? new Date(rawPresence?.start?.date).toLocaleDateString("en-CA") : null,
        TimeIn: rawPresence?.start?.date,
        IN_SignatureFileID: null,
        IN_PhotoFileID: null,
        IN_LocationLat: null,
        IN_LocationLong: null,
        TimeOut: rawPresence?.end?.date,
        OUT_SignatureFileID: null,
        OUT_PhotoFileID: null,
        OUT_LocationLat: null,
        OUT_LocationLong: null
    }];
}

export async function transformSyncAccounts(rawAccount, rawResults) {
    const { classifications, channels, best_times, best_days, frequencies, territories } = rawResults;
    const frequencyMap = mapById(frequencies);
    const classificationMap = mapById(classifications);
    const bestDayMap = mapById(best_days);
    const bestTimeMap = mapById(best_times);
    const territoryMap = mapById(territories);
    const channelMap = mapById(channels);

    return rawAccount.map(cust => {
        return {
            ID: cust?.id,
            Code: cust?.code,
            Name: cust?.name,
            Specialty: "",
            Frequency_code: frequencyMap[cust.frequencyID]?.code,
            Frequency_count: frequencyMap[cust.frequencyID]?.count,
            Class: classificationMap[cust.classificationID]?.name,
            Time_Start: bestTimeMap[cust.timeScheduleID]?.time?.start,
            Time_End: bestTimeMap[cust.timeScheduleID]?.time?.end,
            Time_call: bestTimeMap[cust.timeScheduleID]?.description,
            Day_call: bestDayMap[cust.dayScheduleID]?.code,
            InstitutionID: cust?.institutionID,
            TerritoryID: cust?.territoryID,
            Area: territoryMap[cust.territoryID]?.description,
            Contact_No: null,
            Type: customerAccountType[cust.type],
            AddressDetail: cust?.address
        };
    });
}

export async function transformSyncItems(rawItems) {
    return rawItems.map(item => {
        return {
            ID: item?.id,
            Code: item?.code,
            Description: item?.description,
            ShortName: item?.sh_name,
            LongName: item?.l_name,
            DefaultPrice: item?.price,
            ItemClassificationID: item?.classification_id,
            UnitID: item?.defaultUnit,
            Minimum: 0,
            Status: 1
        };
    });
}

export async function transformSyncUnits(rawUnits) {
    return rawUnits.map(unit => {
        return {
            ID: unit?.id,
            Code: unit?.code,
            Description: unit?.description,
            ClassificationID: unit?.classification
        };
    });
}

export async function transformSyncReasons(rawReasons) {
    return rawReasons.map(reason => {
        return {
            ID: reason?.id,
            Code: reason?.code,
            Description: reason?.description,
            RequireRemarks: reason?.requireRemarks,
            RecoveryState: reason?.recoveryState
        };
    });
}

export async function transformSyncHolidays(rawHolidays) {
    return rawHolidays.map(holiday => {
        return {
            ID: holiday?.id,
            Date: holiday?.date,
            Description: holiday?.description
        };
    });
}

export async function transformSyncCycle(rawCycle) {
    return rawCycle.map(cycle => {
        return {
            ID: cycle?.id,
            Code: cycle?.code,
            Description: cycle?.description,
            StartDate: cycle?.range?.start,
            EndDate: cycle?.range?.end,
            SubCycle: cycle?.sub_cycles?.map(sub => ({
                Index: sub?.index,
                StartDate: sub?.range?.start,
                EndDate: sub?.range?.end
            })) || []
        };
    });
}

export async function transformSyncFile(rawFile) {
    const results = [];
    for (const file of rawFile) {
        try {

            if (file.SizeClassification === "large") {
                results.push(file);
            } else if (file.SizeClassification === "small") {
                const response = await fetch(`${appURL.APP_BaseURL}${file.AccessUrl}`);
                if (!response.ok) throw new Error("Network response was not ok");

                const blob = await response.blob();

                results.push({
                    ...file,
                    BlobData: blob,  // store raw blob
                    Status: FileStatus.downloaded,
                });
            }
        } catch (error) {
            console.error(`Failed to fetch file ${file?.FileName}:`, error);
            results.push(file);
        }
    }

    return results;
}

export async function transformSyncTerritories(rawTerritories) {
    return rawTerritories.map(territory => {
        return {
            ID: territory?.id,
            AgentID: territory?.agentID,
            Code: territory?.code,
            Name: territory?.description
        }
    })
}

export async function transformInstitutionMapping(rawInstitution) {
    const institution = rawInstitution.map(t => ({
        ID: t?.id,
        Code: t?.code,
        Name: t?.name,
        Address: t?.address
    }));

    return institution;
}

export async function transformSyncWorksheets(rawWorkSheet) {
    if (!Array.isArray(rawWorkSheet)) return [];

    return rawWorkSheet.map(worksheet => ({
        ID: worksheet?.id ?? null,
        EmployeeName: worksheet?.employee?.name ?? null,
        PlanNumber: worksheet?.planNumber ?? null,
        Cycle: worksheet?.cycle
            ? {
                ID: worksheet?.cycle?.id ?? null,
                Code: worksheet?.cycle?.code ?? null,
                Description: worksheet?.cycle?.description ?? null,
                StartDate: worksheet?.cycle?.start ?? null,
                EndDate: worksheet?.cycle?.end ?? null
            }
            : null,
        Status: planWorksheetStatusMapping[worksheet?.status] ?? null,
        Days: worksheet?.days ?? null,
        Statistics: worksheet?.statistics ?? null
    }));
}

export async function transformSyncPolicies(rawPolicies) {
    if (!Array.isArray(rawPolicies)) return [];

    return rawPolicies.map(policy => ({
        ID: policy?.id ?? 1,
        Policies: policy?.policies
    }));
}

export async function transformSyncSessionJointDays(rawJoints) {
    if (!Array.isArray(rawJoints)) return [];

    return rawJoints.map(joint => ({
        ID: joint?.date ?? null,
        Joint: joint?.joint ?? false
    }));
}

export async function transformSyncEvaluationTemplate(rawTemplate) {
    if (!Array.isArray(rawTemplate)) return [];

    return rawTemplate.map(template => ({
        ID: template?.id ?? null,
        Description: template?.description ?? null,
        TemplateJson: template?.template ?? null,
        Version: template?.version ?? null
    }));
}